import { Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import { MultiSelectOptionsQuestion } from "anketa-core/multiselect_options_question";
import { OPSANIO_HARDENING_COMPONENTS } from "src/questionnaires";

/** Option list key per workload, e.g. `opsanio.hardening.codex`. */
const hardeningOptions = (workload: string): string =>
  `opsanio.hardening.${workload}`;

// Component names are technical identifiers - shown as-is in both languages.
Object.entries(OPSANIO_HARDENING_COMPONENTS).forEach(([workload, components]) => {
  components.forEach((component) => {
    MessageCollection.Global.setTranslations(
      `${hardeningOptions(workload)}.${component}`,
      [new Translation("en", component), new Translation("de", component)],
    );
  });
});

/**
 * Container-hardening rollout (noerkelit/aw_anketa#248): per-cluster switches
 * for moving workloads from root to opsanio:opsanio (65532), rendered as
 * `hardening.<workload>.<step>` in the opsanio values.
 *
 * Layout grows without restructuring: one sub-section per workload (codex
 * today), one multi-select per rollout step (fix_perms today, run_as_non_root
 * next), all fed from OPSANIO_HARDENING_COMPONENTS.
 *
 * A factory, not a module-level Section: the catalog is rebuilt per
 * questionnaire and a Section can only have one parent. Visibility and
 * read-only are set by the caller from `opsanio/hardening · view/update`.
 */
export const hardeningSection = (): {
  section: Section;
  questions: MultiSelectOptionsQuestion[];
} => {
  const section = new Section("hardening", [
    new Translation("en", "Hardening"),
    new Translation("de", "Hardening"),
  ]);
  section.description = [
    new Translation(
      "en",
      "Rollout switches for running workloads as non-root (opsanio:opsanio, 65532). Set per cluster by operators - not a product setting.",
    ),
    new Translation(
      "de",
      "Rollout-Schalter für den Betrieb der Workloads als non-root (opsanio:opsanio, 65532). Pro Cluster durch den Betrieb gesetzt - keine Produkteinstellung.",
    ),
  ];

  const codex = new Section("codex", [
    new Translation("en", "codex"),
    new Translation("de", "codex"),
  ]);
  section.addChild(codex);

  const fixPerms = new MultiSelectOptionsQuestion(
    "fix_perms",
    [
      new Translation("en", "Volume ownership migration (fix-perms)"),
      new Translation("de", "Migration der Volume-Besitzrechte (fix-perms)"),
    ],
    hardeningOptions("codex"),
  );
  fixPerms.help = [
    new Translation(
      "en",
      "Components listed here get a one-off ownership migration of their volumes on the next start. Enable one release before switching the component to non-root.",
    ),
    new Translation(
      "de",
      "Die hier ausgewählten Komponenten erhalten beim nächsten Start eine einmalige Migration der Besitzrechte ihrer Volumes. Eine Release vor der Umstellung der Komponente auf non-root aktivieren.",
    ),
  ];
  codex.addChild(fixPerms);

  return { section, questions: [fixPerms] };
};
